"use client"

import { useQuery, type DefaultError, type QueryKey, type UseQueryOptions } from "@tanstack/react-query"

import { useViewer } from "@/platform/session/use-viewer"
import { ANONYMOUS_VIEWER, privateKey, UNRESOLVED_VIEWER } from "@/platform/session/viewer"
import { onlyForViewer } from "@/platform/session/viewer-boundary"

export const usePrivateQuery = <TQueryFnData = unknown, TError = DefaultError, TData = TQueryFnData>(
  options: Omit<UseQueryOptions<TQueryFnData, TError, TData, QueryKey>, "queryKey"> & { queryKey: QueryKey },
  allowAnonymous = false
) => {
  const viewer = useViewer()
  const allowed = viewer !== null && (allowAnonymous || viewer !== ANONYMOUS_VIEWER)
  const queryKey = privateKey(viewer ?? UNRESOLVED_VIEWER, ...options.queryKey)
  const result = useQuery<TQueryFnData, TError, TData, QueryKey>({
    ...options,
    queryKey,
    placeholderData:
      typeof options.placeholderData === "function"
        ? (onlyForViewer(viewer, options.placeholderData as Parameters<typeof onlyForViewer>[1]) as typeof options.placeholderData)
        : options.placeholderData,
    meta: { ...options.meta, ...(viewer === null ? {} : { viewer }) },
    enabled: allowed ? options.enabled : false,
  })

  return { ...result, data: allowed ? result.data : undefined, queryKey }
}
